import type { Route } from 'vue-router'
import { $getCookie, $getQueryParam, $normalizeQueryString } from '@/helpers'
import { useEffectiveSubmitTrackStore } from '@/stores'
import { sleep } from '@/utils'
import { clearEmpty } from '@/utils/clearEmpty'

interface CollectAttributionOptions {
  waitForCookies?: boolean
  syncStore?: boolean
}

/**
 * 通用Cookie轮询函数
 * @param {string[]} cookieNames - 需要获取的Cookie名称数组
 * @param {number} [maxRetries=20] - 最大重试次数（默认20）
 * @param {number} [retryInterval=100] - 重试间隔毫秒数（默认100）
 * @returns {Promise<Object>} - 包含Cookie键值对的对象（未获取到的值为undefined）
 */
async function pollCookies(cookieNames: string[], maxRetries = 20, retryInterval = 100) {
  const results: any = {}
  for (let i = 0; i < maxRetries; i++) {
    cookieNames.forEach((name) => {
      if (!results[name])
        results[name] = $getCookie(name) || undefined
    })
    if (cookieNames.every(name => results[name] !== undefined))
      break
    if (i < maxRetries - 1)
      await sleep(retryInterval)
  }

  return results
}

/**
 * 解码 URL 参数
 * @param {string} value - 需要解码的参数值
 * @returns {string} 解码后的参数值
 */
function decodeParam(value: string): string {
  if (!value)
    return value
  try {
    return decodeURIComponent(value)
  }
  catch {
    // 如果解码失败，返回原值
    return value
  }
}

function getRouteQueryValue(route: Route | undefined, key: string) {
  const value = route?.query?.[key]
  if (Array.isArray(value))
    return value[0] ?? ''
  if (value == null)
    return ''
  return String(value)
}

function getQueryParamValue(key: string, route?: Route) {
  const routeValue = getRouteQueryValue(route, key)
  if (routeValue)
    return decodeParam(routeValue)
  return decodeParam($getQueryParam(key) || '')
}

function getRouteSearchParams(route?: Route) {
  if (!route)
    return new URLSearchParams($normalizeQueryString(window.location.search))

  const searchParams = new URLSearchParams()
  Object.entries(route.query || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item != null && item !== '')
          searchParams.append(key, String(item))
      })
      return
    }

    if (value != null && value !== '')
      searchParams.set(key, String(value))
  })
  return searchParams
}

/**
 * 获取通用的查询参数（所有参数会自动 decode）
 * @returns {Object} 包含所有通用查询参数的对象
 */
export function getCommonQueryParams(route?: Route) {
  return clearEmpty({
    pId: getQueryParamValue('pId', route),
    contentId: getQueryParamValue('contentId', route),
    initIndex: getQueryParamValue('initIndex', route),
    utm_campaign_id: getQueryParamValue('utm_campaign_id', route),
    utm_campaign: getQueryParamValue('utm_campaign', route),
    utm_adset: getQueryParamValue('utm_adset', route),
    utm_adset_id: getQueryParamValue('utm_adset_id', route),
    utm_ad_name: getQueryParamValue('utm_ad_name', route),
    utm_ad_id: getQueryParamValue('utm_ad_id', route),
    utm_source: getQueryParamValue('utm_source', route),
    utm_medium: getQueryParamValue('utm_medium', route),
    utm_site_source_name: getQueryParamValue('utm_site_source_name', route),
    utm_id: getQueryParamValue('utm_id', route),
    utm_content: getQueryParamValue('utm_content', route),
    utm_term: getQueryParamValue('utm_term', route),
    bizAdId: getQueryParamValue('bizAdId', route),
  })
}

function collectTTParams(ttclid: string, _ttp: string, route?: Route) {
  return clearEmpty({
    _fbc: ttclid,
    _fbp: _ttp,
    attributeType: 'web',
    url: decodeParam(window.location.href),
    ua: navigator.userAgent,
    ...getCommonQueryParams(route),
  })
}

function collectFBParams(_fbp: string, _fbc: string, route?: Route) {
  return clearEmpty({
    _fbp,
    _fbc,
    attributeType: 'web',
    url: decodeParam(window.location.href),
    ua: navigator.userAgent,
    ...getCommonQueryParams(route),
  })
}

function collectBizAdParams(route?: Route) {
  return getCommonQueryParams(route)
}

function collectTalentParams(route?: Route) {
  const searchParams = getRouteSearchParams(route)
  const params: Record<string, string> = {}
  searchParams.forEach((value, key) => {
    if (value)
      params[key] = decodeParam(value)
  })
  return params
}

function collectAxonParams(route?: Route) {
  const searchParams = getRouteSearchParams(route)
  const params: Record<string, string> = {}
  searchParams.forEach((value, key) => {
    if (value)
      params[key] = decodeParam(value)
  })

  const isBookOrChapterPage = ['book', 'chapter', 'chapter-scroll'].includes(route?.name as string)
  if (isBookOrChapterPage && route?.params?.contentId)
    params.contentId = String(route.params.contentId)

  params.attributeType = 'web'
  return params
}

export function hasAdAttributionParams(route?: Route) {
  return (
    getQueryParamValue('utm_source', route) === 'axon'
    || getQueryParamValue('utm_source', route) === 'talent'
    || !!getQueryParamValue('bizAdId', route)
    || !!getQueryParamValue('ttclid', route)
    || !!getQueryParamValue('fbclid', route)
  )
}

export async function collectAttributionParams(route?: Route, options: CollectAttributionOptions = {}) {
  const { waitForCookies = true, syncStore = false } = options

  let params: Record<string, any> = {}

  if (getQueryParamValue('utm_source', route) === 'axon') {
    params = collectAxonParams(route)
    if (syncStore)
      useEffectiveSubmitTrackStore().$setParams(params)
    return params
  }

  if (getQueryParamValue('utm_source', route) === 'talent') {
    params = collectTalentParams(route)
    if (syncStore)
      useEffectiveSubmitTrackStore().$setParams(params)
    return params
  }

  if (getQueryParamValue('bizAdId', route))
    params = collectBizAdParams(route)

  if (getQueryParamValue('ttclid', route)) {
    const ttclid = getQueryParamValue('ttclid', route)
    const _ttp = waitForCookies
      ? (await pollCookies(['_ttp'], 20, 100))._ttp
      : ($getCookie('_ttp') || '')
    params = collectTTParams(ttclid, _ttp, route)
  }

  if (getQueryParamValue('fbclid', route)) {
    const cookies = waitForCookies
      ? await pollCookies(['_fbp', '_fbc'], 20, 100)
      : { _fbp: $getCookie('_fbp') || '', _fbc: $getCookie('_fbc') || '' }
    params = collectFBParams(cookies._fbp || '', cookies._fbc || '', route)
  }

  if (syncStore)
    useEffectiveSubmitTrackStore().$setParams(params)

  return params
}

export default function useFacebookTrack() {
  return {
    async init(route?: Route) {
      return collectAttributionParams(route, {
        waitForCookies: true,
        syncStore: true,
      })
    },
  }
}

// tt 链接
// https://h5-fb-test.rocnovel.com/content/228211894582247424/?
// utm_campaign=0313-test
// &utm_adset=0313test
// &utm_campaign_id=1826471701030929
// &utm_adset_id=1826471701030961
// &utm_ad_id=1826471704216625
// &utm_source=tt
// &utm_site_source_name=tt
// &utm_medium=tt
// &utm_ad_name=1_HGDQM4Zu.mp4_2025-03-13%2017%3A30%3A21
// &contentId=228211894582247424
// &initIndex=null
// &pId=231115327815557120
// &ttclid=E_C_P_CsEBuiB9Z7Tl5oEujM4EW7KLVr8lMm_0BQWd-5U9sv-1OjDqq8Ls-e2cYogb5dVaCCkk0_1mWvqMPuj0-3iqquh8EURMnMXbW7UjXTd6JtytI1K8xY14yn5K3zEk5Zd0lV_xRbT6h--bYRoeiZMFaVFr-eeHIulJ-xb7MOxDI5N1EOwGtE56BOo5fYcfH2n-EdgU3LiiMXMrkB8mOtMU5sDC0s6xsIVD_xE7VxMZ_bIBxmjz69ioRyubz2cJYGVlMadLDhIEdjIuMA

// fb 链接
// https://h5-fb-test.rocnovel.com/content/190052388312711168/?
// utm_campaign=Panda_rocnovel_AND_182899821023596544_HYJ03_1125_1014V_test
// &utm_adset=V
// &utm_campaign_id=120213965296190162
// &utm_medium=Facebook_Desktop_Feed
// &utm_adset_id=120213965296440162
// &utm_ad_id=120213965296800162
// &utm_source=fb
// &utm_ad_name=1125-test
// &utm_site_source_name=fb
// &contentId=190052388312711168
// &initIndex=null
// &pId=191922336765050880
// &utm_id=120213965296190162
// &utm_content=120213965296800162
// &utm_term=120213965296440162
// &fbclid=IwY2xjawGxECFleHRuA2FlbQIxMAABHftuWjJ_vybW7NIPAybd0LCDJHwywHTlt_PqpM0YK9gjKRYPBZaGhFgvOA_aem_JN3DZ4ShLxryc8Jdau1ECA

// https://h5-fb-test.rocnovel.com/content/N0vJJlpLhmNfmtwJ/171582742764982272?utm_campaign=3232333&utm_content=32323233&utm_medium=paid&utm_source=axon&utm_term=utm_ter32332&pId=375588088024928256&pixel_code=applovin33
